import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HELP_TOPICS } from "./helpTopics.js";
import "./helpQuestionPanel.style.scss";

const HELP_ENDPOINT = "https://www.fbxchange.com/ReactPHP/supportHelp.php";

const fallbackQuestions = HELP_TOPICS.map((topic, index) => ({
    id: `fallback-${index}`,
    question: topic.questionExamples?.[0] || topic.title,
    answer: (topic.answer || []).join("\n"),
    section: topic.section || "General",
    sortOrder: (index + 1) * 10,
}));

const formatAnswerLines = (answer) =>
    String(answer || "")
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);

const HelpQuestionPanel = ({ manageMode = false, currentUser = null }) => {
    const [questions, setQuestions] = useState([]);
    const [openId, setOpenId] = useState("");
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [newQuestion, setNewQuestion] = useState("");
    const [newAnswer, setNewAnswer] = useState("");
    const [newSection, setNewSection] = useState("General");
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState("");
    const [editId, setEditId] = useState("");

    const userSecurity = String(currentUser?.userContactLevel ?? currentUser?.security ?? "");
    const isAdmin = userSecurity === "1";

    const loadQuestions = useCallback(async () => {
        setLoading(true);
        setLoadError("");

        try {
            const res = await fetch(HELP_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ scriptName: "getHelpQuestions" }),
            });

            const data = await res.json();
            const nextQuestions = Array.isArray(data?.questions) ? data.questions : [];

            if (!data?.success || nextQuestions.length === 0) {
                throw new Error(data?.error || "No common questions found.");
            }

            setQuestions(nextQuestions);
        } catch (err) {
            setQuestions(fallbackQuestions);
            setLoadError("Using built-in questions until supportHelp.php is uploaded.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadQuestions();
    }, [loadQuestions]);

    const sections = useMemo(() => {
        const seen = new Set();
        return questions
            .map((q) => String(q.section || "General"))
            .filter((section) => {
                if (seen.has(section)) return false;
                seen.add(section);
                return true;
            });
    }, [questions]);

    const addQuestion = async () => {
        if (!isAdmin || saving) return;

        const question = newQuestion.trim();
        const answer = newAnswer.trim();
        const section = newSection.trim() || "General";

        if (!question || !answer) {
            setSaveMessage("Question and answer are required.");
            return;
        }

        setSaving(true);
        setSaveMessage("Saving...");

        try {
            const res = await fetch(HELP_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    scriptName: "addHelpQuestion",
                    question,
                    answer,
                    section,
                    userSecurity,
                    createdBy: String(currentUser?.scout ?? currentUser?.userId ?? ""),
                }),
            });

            const data = await res.json();
            if (!data?.success) {
                throw new Error(data?.error || "Could not add question.");
            }

            setNewQuestion("");
            setNewAnswer("");
            setNewSection(section);
            setSaveMessage("Question added.");
            await loadQuestions();
            if (data?.id) setOpenId(String(data.id));
        } catch (err) {
            setSaveMessage(err?.message || "Could not add question.");
        } finally {
            setSaving(false);
        }
    };

    const startEditQuestion = (item) => {
        if (!isAdmin || String(item?.id || "").startsWith("fallback-")) return;

        setEditId(String(item.id));
        setNewSection(String(item.section || "General"));
        setNewQuestion(String(item.question || ""));
        setNewAnswer(String(item.answer || ""));
        setSaveMessage("Editing existing question.");
    };

    const cancelEdit = () => {
        setEditId("");
        setNewQuestion("");
        setNewAnswer("");
        setNewSection("General");
        setSaveMessage("");
    };

    const saveEditQuestion = async () => {
        if (!isAdmin || saving || !editId) return;

        const question = newQuestion.trim();
        const answer = newAnswer.trim();
        const section = newSection.trim() || "General";

        if (!question || !answer) {
            setSaveMessage("Question and answer are required.");
            return;
        }

        setSaving(true);
        setSaveMessage("Saving changes...");

        try {
            const res = await fetch(HELP_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    scriptName: "updateHelpQuestion",
                    id: editId,
                    question,
                    answer,
                    section,
                    userSecurity,
                }),
            });

            const data = await res.json();
            if (!data?.success) {
                throw new Error(data?.error || "Could not update question.");
            }

            setSaveMessage("Question updated.");
            setEditId("");
            setNewQuestion("");
            setNewAnswer("");
            setNewSection(section);
            await loadQuestions();
            if (data?.id) setOpenId(String(data.id));
        } catch (err) {
            setSaveMessage(err?.message || "Could not update question.");
        } finally {
            setSaving(false);
        }
    };

    if (manageMode) {
        return (
            <div className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
                <div className="border rounded p-3 bg-light mb-2">
                    <div className="fw-bold mb-2">
                        {editId ? "Edit Common Question" : "Add Common Question"}
                    </div>

                    {!isAdmin ? (
                        <div className="alert alert-warning mb-0">Admin access is required.</div>
                    ) : (
                        <>
                            <div className="row g-2 mb-2">
                                <div className="col-16">
                                    <label className="form-label small fw-bold mb-1">Section</label>
                                    <input
                                        className="form-control form-control-sm"
                                        value={newSection}
                                        onChange={(e) => setNewSection(e.target.value)}
                                        placeholder="General"
                                    />
                                </div>
                                <div className="col-32">
                                    <label className="form-label small fw-bold mb-1">Question</label>
                                    <input
                                        className="form-control form-control-sm"
                                        value={newQuestion}
                                        onChange={(e) => setNewQuestion(e.target.value)}
                                        placeholder="Example: How do I reset a window?"
                                    />
                                </div>
                            </div>

                            <label className="form-label small fw-bold mb-1">Answer</label>
                            <textarea
                                className="form-control mb-2"
                                rows="6"
                                value={newAnswer}
                                onChange={(e) => setNewAnswer(e.target.value)}
                                placeholder="Put each step on its own line."
                            />

                            <div className="d-flex justify-content-between align-items-center">
                                <div className="small text-muted">{saveMessage}</div>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={editId ? saveEditQuestion : addQuestion}
                                    disabled={saving}
                                >
                                    {saving ? "Saving..." : editId ? "Save Changes" : "Add Question"}
                                </button>
                                {editId && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm ms-2"
                                        onClick={cancelEdit}
                                        disabled={saving}
                                    >
                                        Cancel Edit
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className="fw-bold small mb-1">Current Common Questions</div>
                <div className="border rounded bg-white flex-grow-1 p-2" style={{ overflowY: "auto", minHeight: 0 }}>
                    {questions.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            className={`btn btn-sm w-100 text-start border-bottom rounded-0 support-help-manage-row ${
                                String(editId) === String(item.id) ? "btn-primary" : "btn-light"
                            }`}
                            onClick={() => startEditQuestion(item)}
                            disabled={String(item.id || "").startsWith("fallback-")}
                        >
                            <span className={String(editId) === String(item.id) ? "text-white-50" : "text-muted"}>
                                {item.section || "General"}:{" "}
                            </span>
                            <span className="fw-bold">{item.question}</span>
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                    <div className="fw-bold">Common Questions</div>
                    <div className="text-muted small">Choose a question to see the answer.</div>
                </div>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadQuestions}>
                    Refresh
                </button>
            </div>

            {loadError && <div className="alert alert-warning py-1 px-2 small mb-2">{loadError}</div>}

            {loading ? (
                <div className="text-muted d-flex align-items-center justify-content-center h-100">
                    Loading common questions...
                </div>
            ) : (
                <div className="border rounded bg-light flex-grow-1 p-2" style={{ overflowY: "auto", minHeight: 0 }}>
                    {sections.map((section) => (
                        <div key={section} className="mb-2">
                            <div className="fw-bold text-muted small mb-1">{section}</div>
                            {questions
                                .filter((item) => String(item.section || "General") === section)
                                .map((item) => {
                                    const isOpen = String(openId) === String(item.id);
                                    return (
                                        <div key={item.id} className="mb-1">
                                            <button
                                                type="button"
                                                className={`btn btn-sm w-100 text-start support-help-question-row ${
                                                    isOpen ? "btn-primary" : "btn-outline-primary bg-white"
                                                }`}
                                                onClick={() => setOpenId(isOpen ? "" : String(item.id))}
                                            >
                                                {item.question}
                                            </button>
                                            {isOpen && (
                                                <div className="border border-top-0 rounded-bottom bg-white p-3">
                                                    <ol className="ps-3 mb-0">
                                                        {formatAnswerLines(item.answer).map((line) => (
                                                            <li key={line} className="mb-2 small">
                                                                {line}
                                                            </li>
                                                        ))}
                                                    </ol>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default HelpQuestionPanel;
